// Vista del coach: roster de miembros + stats agregados del team.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useT } from '../../lib/i18n';
import { supabase, SUPABASE_ENABLED } from '../../lib/supabase';
import { TeamContext, TeamMemberWithStats, PendingInvite } from '../../hooks/useTeam';
import { CoachPlayerView } from './CoachPlayerView';
import { TeamComparisonTable } from './TeamComparisonTable';
import { TeamLeaksPanel } from './TeamLeaksPanel';
import { BenchmarkEditorPanel } from './BenchmarkEditorPanel';
import { payloadToDerived } from './payloadToStats';

interface TeamStatsRow {
  userId:       string | null;
  handsCount:   number;
  updatedAt:    string;
  statsPayload: Record<string, any>;
  syncCount:    number;
}

interface Props {
  ctx:               TeamContext;
  loadDashboardData: (teamId: string) => Promise<{ memberships: TeamMemberWithStats[]; activeSeats: number; pendingSeats: number; pendingInvites: PendingInvite[] } | null>;
  revokeInvite:      (inviteId: string) => Promise<boolean>;
  transferOwnership: (teamId: string, newOwnerId: string) => Promise<boolean>;
}

export function OwnerDashboardView({ ctx, loadDashboardData, revokeInvite, transferOwnership }: Props) {
  const { t, lang } = useT();
  const { team, membership } = ctx;

  const [loading,         setLoading]         = useState(true);
  const [members,         setMembers]         = useState<TeamMemberWithStats[]>([]);
  const [pendingInvites,  setPendingInvites]  = useState<PendingInvite[]>([]);
  const [activeSeats,     setActiveSeats]     = useState(0);
  const [pendingSeats,    setPendingSeats]    = useState(0);
  const [teamStats,       setTeamStats]       = useState<TeamStatsRow[]>([]);
  const [inviteEmail,      setInviteEmail]      = useState('');
  const [invitePlayerName, setInvitePlayerName] = useState('');
  const [inviting,         setInviting]         = useState(false);
  const [inviteMsg,       setInviteMsg]       = useState<{ type: 'ok' | 'warn' | 'err'; text: string } | null>(null);
  const [inviteLink,      setInviteLink]      = useState<string | null>(null);
  const [linkCopied,      setLinkCopied]      = useState(false);
  const [removeConfirm,   setRemoveConfirm]   = useState<string | null>(null);
  const [revoking,        setRevoking]        = useState<string | null>(null);
  const [transferTo,          setTransferTo]          = useState<string | null>(null);
  const [transferring,        setTransferring]        = useState(false);
  const [selectedUserId,      setSelectedUserId]      = useState<string | null | undefined>(undefined);
  const [showBenchmarks,      setShowBenchmarks]      = useState(false);

  const totalUsed   = activeSeats + pendingSeats;
  const seatsFull   = totalUsed >= team.seatsTotal;
  const overAlloc   = totalUsed > team.seatsTotal;

  const loadStats = useCallback(async () => {
    if (!SUPABASE_ENABLED) return;
    const { data } = await supabase
      .from('team_stats')
      .select('user_id, hands_count, updated_at, stats_payload, sync_count')
      .eq('team_id', team.id);
    setTeamStats((data || []).map((r: any) => ({
      userId:       r.user_id,
      handsCount:   r.hands_count,
      updatedAt:    r.updated_at,
      statsPayload: r.stats_payload,
      syncCount:    r.sync_count,
    })));
  }, [team.id]);

  const reload = useCallback(async () => {
    setLoading(true);
    const [data] = await Promise.all([
      loadDashboardData(team.id),
      loadStats(),
    ]);
    if (data) {
      setMembers(data.memberships);
      setActiveSeats(data.activeSeats);
      setPendingSeats(data.pendingSeats);
      setPendingInvites(data.pendingInvites);
    }
    setLoading(false);
  }, [team.id, loadDashboardData, loadStats]);

  useEffect(() => { reload(); }, [reload]);

  // ── Invitar ──────────────────────────────────────────────────────────────────

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim()) return;

    setInviting(true);
    setInviteMsg(null);

    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const jwt = sessionData?.session?.access_token;
      if (!jwt) throw new Error('No session');

      const apiBase = process.env.REACT_APP_API_URL || '';
      const res = await fetch(`${apiBase}/api/teams/invite`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
        body: JSON.stringify({ teamId: team.id, email: inviteEmail.trim(), lang, playerName: invitePlayerName.trim() || undefined }),
      });

      const data = await res.json();

      if (!res.ok) {
        if (data.error === 'team_seats_exceeded') {
          setInviteMsg({ type: 'err', text: t('teams.inviteErrorSeats', { total: String(team.seatsTotal) }) });
        } else if (data.error === 'already_member') {
          setInviteMsg({ type: 'err', text: t('teams.inviteErrorAlready') });
        } else {
          setInviteMsg({ type: 'err', text: t('teams.inviteErrorGeneral') });
        }
        return;
      }

      if (data.emailSent === false) {
        setInviteMsg({ type: 'warn', text: t('teams.inviteSuccessNoEmail', { email: inviteEmail.trim() }) });
      } else {
        setInviteMsg({ type: 'ok', text: t('teams.inviteSuccess', { email: inviteEmail.trim() }) });
      }
      setInviteLink(data.inviteUrl);
      setInviteEmail('');
      setInvitePlayerName('');
      reload();
    } catch {
      setInviteMsg({ type: 'err', text: t('teams.inviteErrorGeneral') });
    } finally {
      setInviting(false);
    }
  };

  // ── Remover miembro ──────────────────────────────────────────────────────────

  const handleRemove = async (userId: string) => {
    if (!SUPABASE_ENABLED) return;
    await supabase
      .from('team_memberships')
      .update({ status: 'left', left_at: new Date().toISOString(), sync_enabled: false })
      .eq('team_id', team.id)
      .eq('user_id', userId);
    await supabase.from('team_audit_log').insert({
      team_id:        team.id,
      actor_user_id:  membership.userId,
      target_user_id: userId,
      action:         'member_removed',
    });
    setRemoveConfirm(null);
    reload();
  };

  const handleCopyLink = () => {
    if (inviteLink) {
      navigator.clipboard.writeText(inviteLink);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    }
  };

  // ── Revocar invitación pendiente ─────────────────────────────────────────────

  const handleRevokeInvite = async (inviteId: string) => {
    setRevoking(inviteId);
    const ok = await revokeInvite(inviteId);
    if (ok) reload();
    else setRevoking(null);
  };

  // ── Transferir ownership ─────────────────────────────────────────────────────

  const handleTransferOwnership = async () => {
    if (!transferTo) return;
    setTransferring(true);
    const ok = await transferOwnership(team.id, transferTo);
    setTransferring(false);
    setTransferTo(null);
    if (ok) reload();
  };

  // ── Stats agregados del team ─────────────────────────────────────────────────

  const totalHands = teamStats.reduce((acc, r) => acc + r.handsCount, 0);
  const syncedCount = members.filter(m => m.membership.syncEnabled && m.membership.status === 'active').length;
  const noSyncCount = members.filter(m => !m.membership.syncEnabled && m.membership.status === 'active').length;

  // Etiqueta de display para un miembro: nombre configurable > email > fallback
  const memberLabel = useCallback((m: TeamMemberWithStats | undefined): string => {
    if (!m) return '—';
    return m.displayName ?? m.email ?? '—';
  }, []);

  // Filas para la tabla comparativa: unir teamStats con label del roster
  const comparisonRows = useMemo(() => {
    return teamStats.map(row => {
      const member = members.find(m => m.membership.userId === row.userId);
      return {
        userId:       row.userId,
        email:        member ? memberLabel(member) : null,
        handsCount:   row.handsCount,
        lastSync:     row.updatedAt,
        statsPayload: row.statsPayload,
      };
    });
  }, [teamStats, members, memberLabel]);

  // Datos del jugador seleccionado para el modal de coaching
  const selectedPlayerData = useMemo(() => {
    if (selectedUserId === undefined) return null;
    const row = teamStats.find(r => r.userId === selectedUserId);
    if (!row) return null;
    const derived = payloadToDerived(row.statsPayload);
    if (!derived) return null;
    const member = members.find(m => m.membership.userId === selectedUserId);
    return { email: member ? memberLabel(member) : '—', handsCount: row.handsCount, lastSync: row.updatedAt, derived };
  }, [selectedUserId, teamStats, members, memberLabel]);

  // ── Estilos ──────────────────────────────────────────────────────────────────

  const card: React.CSSProperties = {
    background: '#111520',
    border: '1px solid #1e2636',
    borderRadius: '8px',
    padding: '1.5rem',
    marginBottom: '1rem',
    fontFamily: 'var(--mono)',
  };

  const sectionLabel = (text: string) => (
    <div style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1rem' }}>
      {text}
    </div>
  );

  if (loading) {
    return (
      <div style={{ textAlign: 'center', color: 'var(--muted)', padding: '3rem', fontFamily: 'var(--mono)' }}>
        {t('teams.loading')}
      </div>
    );
  }

  return (
    <>
    <div style={{ maxWidth: '800px', margin: '0 auto' }}>

      {/* Header */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.75rem' }}>
          <div>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '2px', marginBottom: '0.5rem', textTransform: 'uppercase' }}>
              {t('teams.dashboardTitle')}
            </div>
            <h2 style={{ margin: 0, fontSize: '1.2rem', color: 'var(--text)', letterSpacing: '1px' }}>{team.name}</h2>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '0.7rem', color: overAlloc ? '#e05252' : 'var(--muted)', letterSpacing: '1px' }}>{t('teams.seatsLabel')}</div>
            <div style={{ fontSize: '1.1rem', color: overAlloc ? '#e05252' : 'var(--text)', fontWeight: 700 }}>
              {totalUsed} / {team.seatsTotal}
            </div>
            {(activeSeats > 0 || pendingSeats > 0) && (
              <div style={{ fontSize: '0.68rem', color: 'var(--muted)', marginTop: '0.2rem' }}>
                {t('teams.seatsActiveInvited', { active: String(activeSeats), invited: String(pendingSeats) })}
              </div>
            )}
          </div>
        </div>

        {/* Over-allocation warning */}
        {overAlloc && (
          <div style={{ marginTop: '1rem', padding: '0.6rem 0.9rem', background: '#e0525215', border: '1px solid #e0525240', borderRadius: '4px', fontSize: '0.78rem', color: '#e05252' }}>
            {t('teams.seatsOverAllocated', { actual: String(totalUsed), contracted: String(team.seatsTotal) })}{' '}
            <a href="mailto:support@grindlog.app?subject=Teams%20seats" style={{ color: '#e05252' }}>support@grindlog.app</a>
          </div>
        )}

        {/* Sync off notice */}
        {noSyncCount > 0 && !overAlloc && (
          <div style={{ marginTop: '1rem', padding: '0.6rem 0.9rem', background: '#e0a85215', border: '1px solid #e0a85240', borderRadius: '4px', fontSize: '0.78rem', color: '#e0a852' }}>
            {t('teams.syncOffNotice', { count: String(noSyncCount) })}
          </div>
        )}
      </div>

      {/* Comparativa del team — A2 */}
      <div style={card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          {sectionLabel(t('teams.comparisonTitle'))}
          {totalHands > 0 && (
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
              {t('teams.teamStatsHands', { count: totalHands.toLocaleString() })} · {syncedCount} sync
            </div>
          )}
        </div>
        {comparisonRows.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>{t('teams.teamStatsEmpty')}</p>
        ) : (
          <TeamComparisonTable
            players={comparisonRows}
            onViewPlayer={userId => setSelectedUserId(userId)}
          />
        )}
      </div>

      {/* Panel de leaks del team — A3 */}
      {comparisonRows.length > 0 && (
        <div style={card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
            {sectionLabel(t('teams.leaksTitle'))}
          </div>
          <TeamLeaksPanel
            players={comparisonRows}
            onViewPlayer={userId => setSelectedUserId(userId)}
          />
        </div>
      )}

      {/* Benchmarks del team */}
      <div style={card}>
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => setShowBenchmarks(v => !v)}
        >
          {sectionLabel(t('teams.benchmarks.sectionTitle'))}
          <span style={{ fontSize: '0.72rem', color: 'var(--muted)', marginTop: '-0.8rem' }}>
            {showBenchmarks ? '▾' : '▸'}
          </span>
        </div>
        {showBenchmarks && <BenchmarkEditorPanel teamId={team.id} />}
      </div>

      {/* Roster */}
      <div style={card}>
        {sectionLabel(t('teams.rosterTitle'))}

        {members.length === 0 ? (
          <p style={{ color: 'var(--muted)', fontSize: '0.82rem' }}>{t('teams.rosterEmpty')}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid #1e2636' }}>
                  {[t('teams.colPlayer'), t('teams.colStatus'), t('teams.colSync'), t('teams.colLastSync'), t('teams.colHands')].map(col => (
                    <th key={col} style={{ textAlign: 'left', padding: '0.5rem 0.75rem', color: 'var(--muted)', fontSize: '0.7rem', letterSpacing: '1px', fontWeight: 400 }}>
                      {col}
                    </th>
                  ))}
                  <th />
                </tr>
              </thead>
              <tbody>
                {members.map(m => (
                  <tr key={m.membership.id} style={{ borderBottom: '1px solid #0d1117' }}>
                    <td style={{ padding: '0.65rem 0.75rem', color: 'var(--text)' }}>
                      <span>{memberLabel(m)}</span>
                      {m.displayName && m.email && (
                        <span style={{ marginLeft: '0.4rem', fontSize: '0.72rem', color: 'var(--muted)' }}>({m.email})</span>
                      )}
                      {m.membership.role === 'owner' && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.65rem', color: '#4a9ae0', letterSpacing: '1px' }}>COACH</span>
                      )}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      <span style={{ color: m.membership.status === 'active' ? '#52c2a0' : '#e0a852', fontSize: '0.75rem' }}>
                        {m.membership.status === 'active' ? t('teams.statusActive') : t('teams.statusInvited')}
                      </span>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem' }}>
                      <span style={{ color: m.membership.syncEnabled ? '#52c2a0' : 'var(--muted)', fontSize: '0.75rem' }}>
                        {m.membership.syncEnabled ? '● ON' : '○ OFF'}
                      </span>
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', color: 'var(--muted)', fontSize: '0.75rem' }}>
                      {m.lastSync ? new Date(m.lastSync).toLocaleDateString() : t('teams.noSyncData')}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', color: 'var(--muted)', fontSize: '0.75rem' }}>
                      {m.handsCount != null ? m.handsCount.toLocaleString() : t('teams.noSyncData')}
                    </td>
                    <td style={{ padding: '0.65rem 0.75rem', textAlign: 'right' }}>
                      {m.membership.role !== 'owner' && (
                        removeConfirm === m.membership.userId
                          ? (
                            <span style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', alignItems: 'center' }}>
                              <span style={{ fontSize: '0.7rem', color: 'var(--muted)' }}>
                                {t('teams.removeMemberConfirm', { email: memberLabel(m) })}
                              </span>
                              <button className="btn btn-danger" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem' }} onClick={() => handleRemove(m.membership.userId)}>
                                {t('teams.removeMemberConfirmBtn')}
                              </button>
                              <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem' }} onClick={() => setRemoveConfirm(null)}>
                                ✕
                              </button>
                            </span>
                          )
                          : (
                            <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem' }} onClick={() => setRemoveConfirm(m.membership.userId)}>
                              {t('teams.removeMemberBtn')}
                            </button>
                          )
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Invitar jugadores */}
      <div style={card}>
        {sectionLabel(t('teams.inviteTitle'))}

        {seatsFull ? (
          <div style={{ padding: '0.75rem 1rem', background: '#e0a85212', border: '1px solid #e0a85235', borderRadius: '4px', fontSize: '0.8rem', color: '#e0a852' }}>
            {t('teams.seatsAtCapacity')}{' '}
            <a href="mailto:support@grindlog.app?subject=Teams%20seats" style={{ color: '#e0a852' }}>support@grindlog.app</a>
          </div>
        ) : (
          <form onSubmit={handleInvite} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                placeholder={t('teams.inviteEmailPlaceholder')}
                required
                style={{
                  flex: 1, minWidth: '200px', background: '#151b2a', border: '1px solid #1e2636',
                  borderRadius: '4px', padding: '0.5rem 0.75rem', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.82rem',
                }}
              />
              <input
                type="text"
                value={invitePlayerName}
                onChange={e => setInvitePlayerName(e.target.value)}
                placeholder={t('teams.inviteNamePlaceholder')}
                style={{
                  flex: 1, minWidth: '150px', background: '#151b2a', border: '1px solid #1e2636',
                  borderRadius: '4px', padding: '0.5rem 0.75rem', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.82rem',
                }}
              />
              <button className="btn btn-primary" type="submit" disabled={inviting} style={{ flexShrink: 0 }}>
                {inviting ? t('teams.inviteSending') : t('teams.inviteSendBtn')}
              </button>
            </div>
            <p style={{ fontSize: '0.7rem', color: 'var(--muted)', margin: 0 }}>{t('teams.inviteNameHelp')}</p>
          </form>
        )}

        {inviteMsg && (
          <p style={{ color: inviteMsg.type === 'ok' ? '#52c2a0' : inviteMsg.type === 'warn' ? '#e0a852' : '#e05252', fontSize: '0.78rem', margin: '0.75rem 0 0' }}>
            {inviteMsg.text}
          </p>
        )}

        {inviteLink && (
          <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.72rem', color: 'var(--muted)' }}>{t('teams.inviteLinkLabel')}:</span>
            <code style={{ fontSize: '0.72rem', color: '#4a9ae0', wordBreak: 'break-all', flex: 1 }}>{inviteLink}</code>
            <button className="btn btn-secondary" style={{ fontSize: '0.7rem', padding: '0.2rem 0.6rem', flexShrink: 0 }} onClick={handleCopyLink}>
              {linkCopied ? t('teams.inviteLinkCopied') : t('teams.inviteLinkCopy')}
            </button>
          </div>
        )}

        {/* Invitaciones pendientes con opción de revocar */}
        {pendingInvites.length > 0 && (
          <div style={{ marginTop: '1.25rem', borderTop: '1px solid #1e2636', paddingTop: '1rem' }}>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '0.6rem' }}>
              {t('teams.pendingInvitesTitle')}
            </div>
            {pendingInvites.map(inv => (
              <div key={inv.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.4rem' }}>
                <span style={{ fontSize: '0.8rem', color: 'var(--text)' }}>
                  {inv.playerName ? `${inv.playerName} (${inv.email})` : inv.email}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <span style={{ fontSize: '0.68rem', color: 'var(--muted)' }}>
                    {t('teams.inviteExpires')} {new Date(inv.expiresAt).toLocaleDateString()}
                  </span>
                  <button
                    className="btn btn-secondary"
                    style={{ fontSize: '0.68rem', padding: '0.15rem 0.5rem', color: '#e0a852', borderColor: '#e0a85260' }}
                    disabled={revoking === inv.id}
                    onClick={() => handleRevokeInvite(inv.id)}
                  >
                    {revoking === inv.id ? '...' : t('teams.inviteRevoke')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Transferir ownership */}
      <div style={{ ...card, borderColor: '#2a1a1a' }}>
        {sectionLabel(t('teams.transferOwnershipTitle'))}
        <p style={{ fontSize: '0.78rem', color: 'var(--muted)', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          {t('teams.transferOwnershipDesc')}
        </p>
        {transferTo === null ? (
          <select
            onChange={e => e.target.value && setTransferTo(e.target.value)}
            defaultValue=""
            style={{ background: '#151b2a', border: '1px solid #1e2636', borderRadius: '4px', padding: '0.4rem 0.6rem', color: 'var(--text)', fontFamily: 'var(--mono)', fontSize: '0.8rem' }}
          >
            <option value="">{t('teams.transferOwnershipSelect')}</option>
            {members.filter(m => m.membership.role === 'player' && m.membership.status === 'active').map(m => (
              <option key={m.membership.userId} value={m.membership.userId}>
                {memberLabel(m)}
              </option>
            ))}
          </select>
        ) : (
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8rem', color: '#e0a852' }}>
              {t('teams.transferOwnershipConfirm', { email: memberLabel(members.find(m => m.membership.userId === transferTo)) })}
            </span>
            <button className="btn btn-danger" onClick={handleTransferOwnership} disabled={transferring} style={{ fontSize: '0.75rem' }}>
              {transferring ? '...' : t('teams.transferOwnershipConfirmBtn')}
            </button>
            <button className="btn btn-secondary" onClick={() => setTransferTo(null)} disabled={transferring} style={{ fontSize: '0.75rem' }}>
              {t('teams.leaveCancelBtn')}
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Modal de stats por jugador — A1 */}
    {selectedUserId !== undefined && selectedPlayerData && (
      <CoachPlayerView
        email={selectedPlayerData.email}
        handsCount={selectedPlayerData.handsCount}
        lastSync={selectedPlayerData.lastSync}
        stats={selectedPlayerData.derived}
        onClose={() => setSelectedUserId(undefined)}
      />
    )}
    </>
  );
}
